import { useState, useEffect, useCallback } from 'react'
import { usePageTitle } from '@components/layout/MainLayout'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  InputAdornment,
  Chip,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Avatar,
  Switch,
  FormControlLabel,
  Tooltip,
  Drawer,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Checkbox,
  FormGroup,
} from '@mui/material'
import {
  Search,
  Refresh,
  FilterList,
  VolumeOff,
  Close,
  Email,
  Person,
  CalendarMonth,
  Login,
  Link as LinkIcon,
  Delete,
  Badge,
  Shield,
  Phone,
  LockReset,
  CardGiftcard,
  Add,
  Cancel,
} from '@mui/icons-material'
import { adminApi } from '@api/endpoints/admin'
import { authApi } from '@api/endpoints/auth'
import { subscriptionsApi } from '@api/endpoints/subscriptions'
import {
  AdminUserDto,
  AdminUsersListParams,
  UserConnectionDto,
} from '@api/types/admin'
import type { AdminSubscriptionCreditDto, CreditStatus } from '@api/types/subscription'
import { UserFlags } from '@api/types/auth'
import { hasUserFlag } from '@utils/userFlags'
import GrantCreditDialog from '../components/GrantCreditDialog'

type SortField = 'DisplayName' | 'Email' | 'CreatedAt' | 'LastLoginAt'

const AVAILABLE_ROLES = ['Admin', 'Moderator', 'User']
const DRAWER_WIDTH = 450

export default function AdminUsersPage() {
  usePageTitle('Users')
  const [users, setUsers] = useState<AdminUserDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [updatingFlags, setUpdatingFlags] = useState<string | null>(null)

  // Pagination
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [totalCount, setTotalCount] = useState(0)

  // Sorting
  const [sortBy, setSortBy] = useState<SortField>('DisplayName')
  const [sortDescending, setSortDescending] = useState(false)

  // Filters
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState<string>('')
  const [activeFilter, setActiveFilter] = useState<boolean | ''>('')
  const [showFilters, setShowFilters] = useState(false)

  // User detail drawer
  const [selectedUser, setSelectedUser] = useState<AdminUserDto | null>(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [connections, setConnections] = useState<UserConnectionDto[]>([])
  const [connectionsLoading, setConnectionsLoading] = useState(false)
  const [updatingStatus, setUpdatingStatus] = useState(false)
  const [updatingRoles, setUpdatingRoles] = useState(false)
  const [editedRoles, setEditedRoles] = useState<string[]>([])

  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)

  // Password reset
  const [sendingPasswordReset, setSendingPasswordReset] = useState(false)
  const [passwordResetSuccess, setPasswordResetSuccess] = useState<string | null>(null)

  // Subscription credits
  const [credits, setCredits] = useState<AdminSubscriptionCreditDto[]>([])
  const [creditsLoading, setCreditsLoading] = useState(false)
  const [grantCreditOpen, setGrantCreditOpen] = useState(false)
  const [revokingCreditId, setRevokingCreditId] = useState<string | null>(null)

  const fetchUsers = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params: AdminUsersListParams = {
        page: page + 1, // API is 1-based
        pageSize,
        sortBy,
        sortDescending,
        search: search || undefined,
        role: roleFilter || undefined,
        isActive: activeFilter === '' ? undefined : activeFilter,
      }
      const response = await adminApi.getUsers(params)
      // Handle both array response and paginated response formats
      const usersData = Array.isArray(response) ? response : (response?.items || [])
      setUsers(usersData)
      setTotalCount(Array.isArray(response) ? usersData.length : (response?.totalCount ?? usersData.length))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch users')
    } finally {
      setLoading(false)
    }
  }, [page, pageSize, sortBy, sortDescending, search, roleFilter, activeFilter])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  const fetchUserConnections = useCallback(async (userId: string) => {
    setConnectionsLoading(true)
    try {
      const data = await adminApi.getUserConnections(userId)
      setConnections(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error('Failed to fetch connections:', err)
      setConnections([])
    } finally {
      setConnectionsLoading(false)
    }
  }, [])

  const fetchUserCredits = useCallback(async (userId: string) => {
    setCreditsLoading(true)
    try {
      const data = await subscriptionsApi.getUserCredits(userId)
      setCredits(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error('Failed to fetch credits:', err)
      setCredits([])
    } finally {
      setCreditsLoading(false)
    }
  }, [])

  const handleRevokeCredit = async (creditId: string) => {
    setRevokingCreditId(creditId)
    try {
      await subscriptionsApi.revokeCredit(creditId)
      setCredits(prev => prev.filter(c => c.id !== creditId))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke credit')
    } finally {
      setRevokingCreditId(null)
    }
  }

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortDescending(!sortDescending)
    } else {
      setSortBy(field)
      setSortDescending(false)
    }
    setPage(0)
  }

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage)
  }

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleSearchKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      setPage(0)
      fetchUsers()
    }
  }

  const handleUserClick = (user: AdminUserDto) => {
    setSelectedUser(user)
    setEditedRoles([...(user.roles || [])])
    setDrawerOpen(true)
    fetchUserConnections(user.id)
    fetchUserCredits(user.id)
  }

  const handleCloseDrawer = () => {
    setDrawerOpen(false)
    setSelectedUser(null)
    setConnections([])
    setCredits([])
    setPasswordResetSuccess(null)
  }

  const handleToggleStatus = async () => {
    if (!selectedUser) return
    setUpdatingStatus(true)
    setError(null)
    try {
      await adminApi.updateUserStatus(selectedUser.id, {
        isActive: !selectedUser.isActive,
      })
      // Update local state
      const updatedUser = { ...selectedUser, isActive: !selectedUser.isActive }
      setSelectedUser(updatedUser)
      setUsers(prev => prev.map(u => u.userId === selectedUser.id ? updatedUser : u))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user status')
    } finally {
      setUpdatingStatus(false)
    }
  }

  const handleRoleChange = (role: string) => {
    setEditedRoles(prev =>
      prev.includes(role)
        ? prev.filter(r => r !== role)
        : [...prev, role]
    )
  }

  const handleSaveRoles = async () => {
    if (!selectedUser) return
    setUpdatingRoles(true)
    setError(null)
    try {
      await adminApi.updateUserRoles(selectedUser.id, { roles: editedRoles })
      // Update local state
      const updatedUser = { ...selectedUser, roles: editedRoles }
      setSelectedUser(updatedUser)
      setUsers(prev => prev.map(u => u.userId === selectedUser.id ? updatedUser : u))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user roles')
    } finally {
      setUpdatingRoles(false)
    }
  }

  const handleToggleSilentAdventuresFlag = async (user: AdminUserDto) => {
    setUpdatingFlags(user.id)
    setError(null)

    try {
      const hasSilentFlag = hasUserFlag(user.userFlags, UserFlags.CanCreateSilentAdventures)

      if (hasSilentFlag) {
        await adminApi.updateUserFlags(user.id, {
          removeFlags: UserFlags.CanCreateSilentAdventures,
        })
      } else {
        await adminApi.updateUserFlags(user.id, {
          addFlags: UserFlags.CanCreateSilentAdventures,
        })
      }

      // Update local state
      const newFlags = hasSilentFlag
        ? user.userFlags & ~UserFlags.CanCreateSilentAdventures
        : user.userFlags | UserFlags.CanCreateSilentAdventures

      setUsers(prev => prev.map(u => {
        if (u.userId === user.id) {
          return { ...u, userFlags: newFlags }
        }
        return u
      }))

      if (selectedUser?.userId === user.id) {
        setSelectedUser(prev => prev ? { ...prev, userFlags: newFlags } : null)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user flags')
    } finally {
      setUpdatingFlags(null)
    }
  }

  const handleDeleteUser = async () => {
    if (!selectedUser) return
    setDeleting(true)
    setError(null)
    try {
      await adminApi.deleteUser(selectedUser.id)
      setDeleteDialogOpen(false)
      handleCloseDrawer()
      fetchUsers()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user')
    } finally {
      setDeleting(false)
    }
  }

  const handleSendPasswordReset = async (method: 'email' | 'sms') => {
    if (!selectedUser) return
    setSendingPasswordReset(true)
    setError(null)
    setPasswordResetSuccess(null)
    try {
      await authApi.forgotPassword({
        email: method === 'email' ? selectedUser.email : undefined,
        phoneNumber: method === 'sms' ? selectedUser.phoneNumber : undefined,
        method,
      })
      setPasswordResetSuccess(`Password reset ${method === 'email' ? 'email' : 'SMS'} sent successfully`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send password reset')
    } finally {
      setSendingPasswordReset(false)
    }
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A'
    const date = new Date(dateStr)
    return isNaN(date.getTime()) ? 'N/A' : date.toLocaleString()
  }

  const rolesChanged = selectedUser && JSON.stringify([...editedRoles].sort()) !== JSON.stringify([...(selectedUser.roles || [])].sort())

  return (
    <Box>
      {/* Search and Filters */}
      <Paper sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            size="small"
            sx={{ minWidth: 300, flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant={showFilters ? 'contained' : 'outlined'}
            startIcon={<FilterList />}
            onClick={() => setShowFilters(!showFilters)}
          >
            Filters
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchUsers}
          >
            Refresh
          </Button>
        </Box>

        {showFilters && (
          <Box sx={{ display: 'flex', gap: 2, mt: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Role</InputLabel>
              <Select
                value={roleFilter}
                label="Role"
                onChange={(e) => {
                  setRoleFilter(e.target.value)
                  setPage(0)
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Admin">Admin</MenuItem>
                <MenuItem value="Moderator">Moderator</MenuItem>
                <MenuItem value="User">User</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={activeFilter}
                label="Status"
                onChange={(e) => {
                  setActiveFilter(e.target.value as boolean | '')
                  setPage(0)
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value={true as any}>Active</MenuItem>
                <MenuItem value={false as any}>Inactive</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="text"
              onClick={() => {
                setSearch('')
                setRoleFilter('')
                setActiveFilter('')
                setPage(0)
              }}
            >
              Clear Filters
            </Button>
          </Box>
        )}
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.main' }}>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortBy === 'DisplayName'}
                    direction={sortBy === 'DisplayName' ? (sortDescending ? 'desc' : 'asc') : 'asc'}
                    onClick={() => handleSort('DisplayName')}
                    sx={{
                      color: 'white !important',
                      '& .MuiTableSortLabel-icon': { color: 'white !important' },
                    }}
                  >
                    User
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortBy === 'Email'}
                    direction={sortBy === 'Email' ? (sortDescending ? 'desc' : 'asc') : 'asc'}
                    onClick={() => handleSort('Email')}
                    sx={{
                      color: 'white !important',
                      '& .MuiTableSortLabel-icon': { color: 'white !important' },
                    }}
                  >
                    Email
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Roles</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                  <TableSortLabel
                    active={sortBy === 'LastLoginAt'}
                    direction={sortBy === 'LastLoginAt' ? (sortDescending ? 'desc' : 'asc') : 'asc'}
                    onClick={() => handleSort('LastLoginAt')}
                    sx={{
                      color: 'white !important',
                      '& .MuiTableSortLabel-icon': { color: 'white !important' },
                    }}
                  >
                    Last Login
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                  <Tooltip title="Permission to create silent adventures">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <VolumeOff fontSize="small" />
                      Silent
                    </Box>
                  </Tooltip>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No users found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow
                    key={user.id}
                    hover
                    onClick={() => handleUserClick(user)}
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          src={user.avatar}
                          sx={{ width: 40, height: 40 }}
                        >
                          {user.displayName?.charAt(0).toUpperCase() || 'U'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {user.displayName}
                          </Typography>
                          {user.userName && (
                            <Typography variant="caption" color="text.secondary">
                              @{user.userName}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{user.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {(user.roles || []).map((role) => (
                          <Chip
                            key={role}
                            label={role}
                            size="small"
                            color={role === 'Admin' ? 'error' : role === 'Moderator' ? 'warning' : 'default'}
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.isActive ? 'Active' : 'Inactive'}
                        color={user.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(user.lastLoginAt)}
                      </Typography>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Switch
                        checked={hasUserFlag(user.userFlags, UserFlags.CanCreateSilentAdventures)}
                        onChange={() => handleToggleSilentAdventuresFlag(user)}
                        disabled={updatingFlags === user.id}
                        color="primary"
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={pageSize}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handlePageSizeChange}
        />
      </Paper>

      {/* User Detail Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        sx={{
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            maxWidth: '90vw',
          },
        }}
      >
        {selectedUser && (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6">User Details</Typography>
              <IconButton onClick={handleCloseDrawer}>
                <Close />
              </IconButton>
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
              {/* User Info */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar
                  src={selectedUser.avatar}
                  sx={{ width: 80, height: 80 }}
                >
                  {selectedUser.displayName?.charAt(0).toUpperCase() || 'U'}
                </Avatar>
                <Box>
                  <Typography variant="h6">{selectedUser.displayName}</Typography>
                  {selectedUser.userName && (
                    <Typography variant="body2" color="text.secondary">
                      @{selectedUser.userName}
                    </Typography>
                  )}
                </Box>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Basic Info */}
              <List dense>
                <ListItem>
                  <ListItemIcon><Email /></ListItemIcon>
                  <ListItemText primary="Email" secondary={selectedUser.email} />
                </ListItem>
                {selectedUser.firstName && (
                  <ListItem>
                    <ListItemIcon><Person /></ListItemIcon>
                    <ListItemText primary="Name" secondary={`${selectedUser.firstName} ${selectedUser.lastName || ''}`} />
                  </ListItem>
                )}
                <ListItem>
                  <ListItemIcon><CalendarMonth /></ListItemIcon>
                  <ListItemText primary="Joined" secondary={formatDate(selectedUser.createdAt)} />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Login /></ListItemIcon>
                  <ListItemText primary="Last Login" secondary={formatDate(selectedUser.lastLoginAt)} />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Badge /></ListItemIcon>
                  <ListItemText primary="User ID" secondary={selectedUser.id} primaryTypographyProps={{ variant: 'body2' }} secondaryTypographyProps={{ variant: 'caption', sx: { wordBreak: 'break-all' } }} />
                </ListItem>
                {selectedUser.phoneNumber && (
                  <ListItem>
                    <ListItemIcon><Phone /></ListItemIcon>
                    <ListItemText primary="Phone" secondary={selectedUser.phoneNumber} />
                  </ListItem>
                )}
              </List>

              <Divider sx={{ my: 2 }} />

              {/* Status */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>Account Status</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Chip
                    label={selectedUser.isActive ? 'Active' : 'Inactive'}
                    color={selectedUser.isActive ? 'success' : 'default'}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    color={selectedUser.isActive ? 'warning' : 'success'}
                    onClick={handleToggleStatus}
                    disabled={updatingStatus}
                  >
                    {updatingStatus ? 'Updating...' : selectedUser.isActive ? 'Deactivate' : 'Activate'}
                  </Button>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Password Reset */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LockReset fontSize="small" /> Password Reset
                </Typography>
                {passwordResetSuccess && (
                  <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPasswordResetSuccess(null)}>
                    {passwordResetSuccess}
                  </Alert>
                )}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<Email />}
                    onClick={() => handleSendPasswordReset('email')}
                    disabled={sendingPasswordReset || !selectedUser.email}
                  >
                    {sendingPasswordReset ? 'Sending...' : 'Send Email'}
                  </Button>
                  {selectedUser.phoneNumber && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Phone />}
                      onClick={() => handleSendPasswordReset('sms')}
                      disabled={sendingPasswordReset}
                    >
                      {sendingPasswordReset ? 'Sending...' : 'Send SMS'}
                    </Button>
                  )}
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Roles */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Shield fontSize="small" /> Roles
                </Typography>
                <FormGroup>
                  {AVAILABLE_ROLES.map((role) => (
                    <FormControlLabel
                      key={role}
                      control={
                        <Checkbox
                          checked={editedRoles.includes(role)}
                          onChange={() => handleRoleChange(role)}
                          disabled={updatingRoles}
                        />
                      }
                      label={role}
                    />
                  ))}
                </FormGroup>
                {rolesChanged && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSaveRoles}
                    disabled={updatingRoles}
                    sx={{ mt: 1 }}
                  >
                    {updatingRoles ? 'Saving...' : 'Save Roles'}
                  </Button>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Flags */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>User Flags</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={hasUserFlag(selectedUser.userFlags, UserFlags.CanCreateSilentAdventures)}
                      onChange={() => handleToggleSilentAdventuresFlag(selectedUser)}
                      disabled={updatingFlags === selectedUser.id}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <VolumeOff fontSize="small" />
                      Can Create Silent Adventures
                    </Box>
                  }
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Subscription Credits */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CardGiftcard fontSize="small" /> Subscription Credits
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<Add />}
                    onClick={() => setGrantCreditOpen(true)}
                  >
                    Grant Credit
                  </Button>
                </Box>
                {creditsLoading ? (
                  <CircularProgress size={20} />
                ) : credits.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No subscription credits</Typography>
                ) : (
                  <List dense>
                    {credits.map((credit) => {
                      const status = (typeof credit.status === 'number'
                        ? ['Pending', 'Redeemed', 'Expired', 'Revoked'][credit.status]
                        : credit.status) as CreditStatus
                      const statusColor = {
                        Pending: 'warning',
                        Redeemed: 'success',
                        Expired: 'default',
                        Revoked: 'error',
                      }[status] as 'warning' | 'success' | 'default' | 'error'

                      return (
                        <ListItem
                          key={credit.id}
                          secondaryAction={
                            status === 'Pending' ? (
                              <Tooltip title="Revoke credit">
                                <IconButton
                                  edge="end"
                                  size="small"
                                  color="error"
                                  onClick={() => handleRevokeCredit(credit.id)}
                                  disabled={revokingCreditId === credit.id}
                                >
                                  {revokingCreditId === credit.id ? <CircularProgress size={16} /> : <Cancel />}
                                </IconButton>
                              </Tooltip>
                            ) : undefined
                          }
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2">
                                  {credit.plan.displayName} — {credit.creditMonths}mo
                                </Typography>
                                <Chip label={status} size="small" color={statusColor} variant="outlined" />
                              </Box>
                            }
                            secondary={
                              <>
                                {credit.reason}
                                {' · '}
                                {new Date(credit.grantedAt).toLocaleDateString()}
                                {credit.grantedByUserName && ` · by ${credit.grantedByUserName}`}
                              </>
                            }
                          />
                        </ListItem>
                      )
                    })}
                  </List>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Connections */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LinkIcon fontSize="small" /> Connected Accounts
                </Typography>
                {connectionsLoading ? (
                  <CircularProgress size={20} />
                ) : connections.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No connected accounts</Typography>
                ) : (
                  <List dense>
                    {connections.map((conn) => (
                      <ListItem key={conn.id}>
                        <ListItemText
                          primary={conn.provider}
                          secondary={conn.providerEmail || conn.providerUsername || conn.providerUserId}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            </Box>

            {/* Footer */}
            <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
              <Button
                variant="outlined"
                color="error"
                startIcon={<Delete />}
                onClick={() => setDeleteDialogOpen(true)}
                fullWidth
              >
                Delete User
              </Button>
            </Box>
          </Box>
        )}
      </Drawer>

      {/* Grant Credit Dialog */}
      {selectedUser && (
        <GrantCreditDialog
          open={grantCreditOpen}
          onClose={() => setGrantCreditOpen(false)}
          userId={selectedUser.id}
          onSuccess={() => fetchUserCredits(selectedUser.id)}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{selectedUser?.displayName}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
